// ─── Route Paths ─────────────────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME: '/',
  ROUTES: '/routes',
  BOOKING: '/booking',
  FLEET: '/fleet',
  TRAVEL_GUIDE: '/travel-guide',
  TWIGA_MILES: '/twiga-miles',
  CARGO: '/cargo',
  ABOUT: '/about',
  NEWS: '/news',
  CONTACT: '/contact',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Destination {
  id: string;
  name: string;
  nameEn: string;
  code: string;
  tagline: string;
  image: string;
  description: string;
  bestSeason: string;
  climate: string;
  highlights: string[];
}

export interface FlightSchedule {
  flightNo: string;
  from: string;
  fromCode: string;
  to: string;
  toCode: string;
  departure: string;
  arrival: string;
  days: string[];
  aircraft: string;
}

export interface FleetItem {
  id: string;
  model: string;
  count: number;
  businessClass: number;
  economyClass: number;
  routes: string;
  image: string;
  description: string;
  features: string[];
}

export interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

// ─── Navigation ───────────────────────────────────────────────────────────────
export const NAV_ITEMS: NavItem[] = [
  { label: '首页', path: ROUTE_PATHS.HOME },
  {
    label: '航线与目的地',
    path: ROUTE_PATHS.ROUTES,
    children: [
      { label: '广州出发航线', path: ROUTE_PATHS.ROUTES },
      { label: '国际航线网络', path: ROUTE_PATHS.ROUTES },
      { label: '目的地详情', path: ROUTE_PATHS.ROUTES },
    ],
  },
  { label: '航班查询与预订', path: ROUTE_PATHS.BOOKING },
  { label: '机队介绍', path: ROUTE_PATHS.FLEET },
  {
    label: '出行指南',
    path: ROUTE_PATHS.TRAVEL_GUIDE,
    children: [
      { label: '行李规定', path: ROUTE_PATHS.TRAVEL_GUIDE },
      { label: '签证信息', path: ROUTE_PATHS.TRAVEL_GUIDE },
      { label: '值机与登机', path: ROUTE_PATHS.TRAVEL_GUIDE },
      { label: '中转指南', path: ROUTE_PATHS.TRAVEL_GUIDE },
      { label: '机票价格查询', path: ROUTE_PATHS.BOOKING },
    ],
  },
  { label: 'Twiga Miles', path: ROUTE_PATHS.TWIGA_MILES },
  { label: '货运服务', path: ROUTE_PATHS.CARGO },
  { label: '关于我们', path: ROUTE_PATHS.ABOUT },
  { label: '联系我们', path: ROUTE_PATHS.CONTACT },
];

// ─── Flight Schedules ─────────────────────────────────────────────────────────
export const FLIGHT_SCHEDULES: FlightSchedule[] = [
  {
    flightNo: 'TC403',
    from: '广州',
    fromCode: 'CAN',
    to: '桑给巴尔',
    toCode: 'ZNZ',
    departure: '01:10',
    arrival: '06:10',
    days: ['周三', '周五', '周日'],
    aircraft: '波音 787-8 梦想客机',
  },
  {
    flightNo: 'TC403',
    from: '广州',
    fromCode: 'CAN',
    to: '达累斯萨拉姆',
    toCode: 'DAR',
    departure: '01:10',
    arrival: '08:25',
    days: ['周三', '周五', '周日'],
    aircraft: '波音 787-8 梦想客机',
  },
  {
    flightNo: 'TC402',
    from: '达累斯萨拉姆',
    fromCode: 'DAR',
    to: '广州',
    toCode: 'CAN',
    departure: '06:10',
    arrival: '22:10',
    days: ['周二', '周四', '周六'],
    aircraft: '波音 787-8 梦想客机',
  },
];

// ─── Destinations ─────────────────────────────────────────────────────────────
export const DESTINATIONS: Destination[] = [
  {
    id: 'zanzibar',
    name: '桑给巴尔',
    nameEn: 'Zanzibar',
    code: 'ZNZ',
    tagline: '印度洋上的天堂之岛',
    image: '/images/57x42cm(Zanzibar)_ydknHi.jpg',
    description: '碧蓝的海水、洁白的沙滩、历史悠久的石头城，桑给巴尔是非洲东海岸最令人心驰神往的度假胜地。',
    bestSeason: '6月–10月（旱季）',
    climate: '热带海洋性气候，全年温暖',
    highlights: ['石头城（UNESCO世界遗产）', '努瓜威海滩', '辛巴保护区', '香料种植园', '珊瑚礁潜水'],
  },
  {
    id: 'dar',
    name: '达累斯萨拉姆',
    nameEn: 'Dar es Salaam',
    code: 'DAR',
    tagline: '东非最大的港口城市',
    image: '/images/57x42cm(Tanzania Destination)_gYUbNj.jpg',
    description: '坦桑尼亚最大城市，集现代商业与非洲传统文化于一身，是前往坦桑尼亚各地的重要枢纽。',
    bestSeason: '6月–10月',
    climate: '热带季风气候',
    highlights: ['国家博物馆', '卡里雅科市场', '迪尔斯海滩', '达累斯萨拉姆港口', '基利马尼购物中心'],
  },
  {
    id: 'serengeti',
    name: '塞伦盖蒂',
    nameEn: 'Serengeti',
    code: 'SEU',
    tagline: '大迁徙的壮阔舞台',
    image: '/images/57x42cm(Serengeti)_ydknHi.jpg',
    description: '世界自然遗产，每年数百万角马和斑马在此完成壮观的大迁徙，是非洲野生动物摄影的圣地。',
    bestSeason: '7月–10月（迁徙季）',
    climate: '热带草原气候',
    highlights: ['动物大迁徙', '狮子猎食', '热气球游览', '马赛马拉', 'Big Five观赏'],
  },
  {
    id: 'kilimanjaro',
    name: '乞力马扎罗',
    nameEn: 'Kilimanjaro',
    code: 'JRO',
    tagline: '非洲屋脊，云端之巅',
    image: '/images/57x42cm(Kilimanjaro)_ydknHi.jpg',
    description: '非洲最高峰，海拔5895米，是全球最受欢迎的高峰徒步目的地之一，登顶无需专业攀岩技术。',
    bestSeason: '1月–3月 & 6月–10月',
    climate: '高山气候，山顶常年积雪',
    highlights: ['乌胡鲁峰（最高点）', '马钦吉路线', '乞力马扎罗国家公园', '长颈鹿与雪山', '咖啡庄园'],
  },
  {
    id: 'ngorongoro',
    name: '恩戈罗恩戈罗',
    nameEn: 'Ngorongoro',
    code: 'ARK',
    tagline: '世界最大完整火山口',
    image: '/images/57x42cm(Ngorongoro).jpg',
    description: '被称为"非洲伊甸园"，世界最大完整火山口内栖息着约30,000只野生动物，是非洲自然奇观。',
    bestSeason: '全年皆宜，旱季最佳',
    climate: '温和，海拔较高略凉',
    highlights: ['世界最大完整火山口', '黑犀牛', 'Big Five', '马赛族文化', 'Olduvai峡谷（人类起源地）'],
  },
  {
    id: 'seychelles',
    name: '塞舌尔',
    nameEn: 'Seychelles',
    code: 'SEZ',
    tagline: '印度洋上的珍珠群岛',
    image: '/images/April 25x21.5cm (Zanzibar)_ydknHi.jpg',
    description: '由115个岛屿组成的印度洋岛国，拥有世界级的白沙滩、清澈海水和独特的花岗岩地貌，是蜜月旅行者的梦想之地。',
    bestSeason: '4月–5月 & 10月–11月',
    climate: '热带海洋性气候',
    highlights: ['马埃岛', '普拉兰岛海滩', '拉迪格岛', '塞舌尔植物园（海椰子）', '珊瑚礁浮潜'],
  },
];

// ─── Fleet ────────────────────────────────────────────────────────────────────
export const FLEET_ITEMS: FleetItem[] = [
  {
    id: 'b787',
    model: '波音 787-8 梦想客机',
    count: 3,
    businessClass: 22,
    economyClass: 240,
    routes: '广州、迪拜、孟买等洲际航线',
    image: '/images/57x42cm(Dreamliner).jpg',
    description: '采用波音最新一代宽体客机技术，更大的舷窗、更高的客舱气压、更低噪音，带来卓越的长途飞行体验。',
    features: ['22席公务舱（平躺床铺）', '240席经济舱', '先进娱乐系统', '机上Wi-Fi', '全服务餐饮'],
  },
  {
    id: 'b737max9',
    model: '波音 737 MAX 9',
    count: 2,
    businessClass: 16,
    economyClass: 165,
    routes: '区域及国际航线',
    image: '/images/57x42cm(Max 9)_ydknHi.jpg',
    description: '波音最新一代单通道飞机，LEAP-1B发动机降低燃油消耗约20%，更安静、更环保、更高效。',
    features: ['16席公务舱', '165席经济舱', 'Sky Interior客舱设计', '高效LEAP发动机', '宽大储物空间'],
  },
  {
    id: 'a220',
    model: '空客 A220-300',
    count: 4,
    businessClass: 12,
    economyClass: 120,
    routes: '区域及中短程国际航线',
    image: '/images/November 25x21.5cm (Max 9)_ydknHi.jpg',
    description: '庞巴迪C系列技术加持，每座燃油消耗低，宽大座椅（2-3布局）媲美宽体机舒适度，是区域航线首选。',
    features: ['12席公务舱', '120席经济舱', '宽大座椅（比肩宽体机）', '大型舷窗', '低噪音客舱'],
  },
  {
    id: 'q400',
    model: '庞巴迪 Dash 8-Q400',
    count: 5,
    businessClass: 0,
    economyClass: 76,
    routes: '坦桑尼亚国内航线',
    image: '/images/b737max9-side.png',
    description: '专为支线航线设计，适合短距离起降，连接坦桑尼亚各国内目的地，飞抵国家公园入口机场。',
    features: ['76席全经济舱', '支线短距起降', '连接塞伦盖蒂、恩戈罗恩戈罗等', '高效运营', '灵活时刻'],
  },
  {
    id: 'b767f',
    model: '波音 767-300F（全货机）',
    count: 1,
    businessClass: 0,
    economyClass: 0,
    routes: 'Air Tanzania Cargo 货运航线',
    image: '/images/cargo-freighter-crop.jpg',
    description: '专用全货机，执飞Air Tanzania Cargo航线，连接广州、达累斯萨拉姆及非洲各大货运枢纽，是"乞力马扎罗之翼"货运服务的核心力量。',
    features: ['最大载重约54吨', '宽体货机舱', '广州直飞达累斯萨拉姆', '温控特殊货物', '活体动物运输'],
  },
];

// ─── Contact Info ─────────────────────────────────────────────────────────────
export const CONTACT_INFO = {
  phone: '020-38550084',
  passengerEmail: 'service.can@airtanzania.cn',
  agentEmail: 'canto@airtanzania.cn',
  qqGroup: '581674034',
  weibo: 'https://weibo.com/airtanzania',
  address: '广州市天河区林和西路9号耀中广场（ChinaShine Plaza）A座1323室',
  hqAddress: 'P.O.BOX 543, Dar es Salaam, Tanzania',
  hqPhone: '+255 748 773 900',
  hqEmail: 'info@airtanzania.co.tz',
  website: 'https://www.airtanzania.co.tz',
};

// ─── Airline Destinations for Search ─────────────────────────────────────────
export const ALL_DESTINATIONS = [
  { name: '达累斯萨拉姆', code: 'DAR', country: '坦桑尼亚' },
  { name: '桑给巴尔', code: 'ZNZ', country: '坦桑尼亚' },
  { name: '乞力马扎罗', code: 'JRO', country: '坦桑尼亚' },
  { name: '阿鲁沙', code: 'ARK', country: '坦桑尼亚' },
  { name: '姆万扎', code: 'MWZ', country: '坦桑尼亚' },
  { name: '多多马', code: 'DOD', country: '坦桑尼亚' },
  { name: '姆贝亚', code: 'MBI', country: '坦桑尼亚' },
  { name: '迪拜', code: 'DXB', country: '阿联酋' },
  { name: '孟买', code: 'BOM', country: '印度' },
  { name: '约翰内斯堡', code: 'JNB', country: '南非' },
  { name: '内罗毕', code: 'NBO', country: '肯尼亚' },
  { name: '卢本巴希', code: 'FBM', country: '刚果民主共和国' },
  { name: '金沙萨', code: 'FIH', country: '刚果民主共和国' },
  { name: '恩多拉', code: 'NLA', country: '赞比亚' },
  { name: '卢萨卡', code: 'LUN', country: '赞比亚' },
  { name: '哈拉雷', code: 'HRE', country: '津巴布韦' },
  { name: '布琼布拉', code: 'BJM', country: '布隆迪' },
  { name: '恩德培', code: 'EBB', country: '乌干达' },
  { name: '科摩罗', code: 'HAH', country: '科摩罗' },
  { name: '维多利亚瀑布', code: 'VFA', country: '津巴布韦' },
  { name: '开普敦', code: 'CPT', country: '南非' },
  { name: '塞舌尔', code: 'SEZ', country: '塞舌尔' },
];
